import { FunctionComponent, useState, useEffect, useRef } from 'react';
import { faCheck, faCopy, faCircleCheck, faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import { useMarketplace, useRoles } from 'hooks';
import { useApiClientWrapper } from 'hooks';
import { useDispatch } from 'react-redux';
import { changeIntakeFormStatusThunk, saveIntakeFormCustomQuestionsThunk } from '@spike/marketplace-action';
import { IntakeFormCustomQuestion, IntakeFormCustomQuestionCategory } from '@spike/marketplace-model';
import { v4 as uuid } from 'uuid';
import moment from 'moment';

import { Drawer } from 'components/UI/V3/Drawer/Drawer';
import Button from 'components/UI/V3/Button/Button';
import { Switch } from 'components/UI/V3/Forms/Switch';
import AlertMessage from 'components/UI/V3/AlertMessage';

import TextFieldWithButton from 'components/UI/TextFieldWithButton';
import ConfirmClosePopup from 'components/CreateBooking/UI/ConfirmClosePopup';

import CustomQuestions from './ui/CustomQuestions';
import CardOnFile from './ui/CardOnFile';
import MobileCloseButton from './ui/MobileCloseButton';

import styles from './IntakeFormPanel.module.scss';

interface IntakeFormPanelProps {
    isOpen: boolean;
    onClose: () => void;
    onOpenShareSendPanel?: () => void;
}

// Helper function to compare questions (ignoring timestamps and order)
const questionsEqual = (q1: IntakeFormCustomQuestion[], q2: IntakeFormCustomQuestion[]): boolean => {
    if (q1.length !== q2.length) return false;

    const normalizeQuestion = (q: IntakeFormCustomQuestion) => ({
        id: q.id,
        question: q.question.trim(),
        category: q.category,
        required: q.required,
        enabled: q.enabled
    });

    const normalized1 = q1.map(normalizeQuestion).sort((a, b) => (a.id || 0) - (b.id || 0));
    const normalized2 = q2.map(normalizeQuestion).sort((a, b) => (a.id || 0) - (b.id || 0));

    return JSON.stringify(normalized1) === JSON.stringify(normalized2);
};

const DEFAULT_QUESTIONS = (marketplaceId: number): Array<IntakeFormCustomQuestion> => [
    {
        id: 0,
        order: 1,
        uuid: uuid(),
        enabled: true,
        required: true,
        marketplaceId,
        category: IntakeFormCustomQuestionCategory.FOR_CUSTOMERS,
        question: 'What are you looking for in a groomer?',
        createdAt: moment(),
        updatedAt: moment()
    },
    {
        id: 0,
        order: 2,
        uuid: uuid(),
        enabled: true,
        required: true,
        marketplaceId,
        category: IntakeFormCustomQuestionCategory.FOR_CUSTOMERS,
        question: 'What prompted you to look for a new groomer?',
        createdAt: moment(),
        updatedAt: moment()
    },
    {
        id: 0,
        order: 3,
        uuid: uuid(),
        enabled: true,
        required: true,
        marketplaceId,
        category: IntakeFormCustomQuestionCategory.FOR_CUSTOMERS,
        question: 'How did you hear about us?',
        createdAt: moment(),
        updatedAt: moment()
    },
    {
        id: 0,
        order: 4,
        uuid: uuid(),
        enabled: true,
        required: true,
        marketplaceId,
        category: IntakeFormCustomQuestionCategory.FOR_PETS,
        question: 'How often is your pet professionally groomed?',
        createdAt: moment(),
        updatedAt: moment()
    },
    {
        id: 0,
        order: 5,
        uuid: uuid(),
        enabled: true,
        required: true,
        marketplaceId,
        category: IntakeFormCustomQuestionCategory.FOR_PETS,
        question: 'Does your pet have any grooming-related fears or anxieties?',
        createdAt: moment(),
        updatedAt: moment()
    },
    {
        id: 0,
        order: 6,
        uuid: uuid(),
        enabled: true,
        required: true,
        marketplaceId,
        category: IntakeFormCustomQuestionCategory.FOR_PETS,
        question: 'Does your pet take medication or have any health conditions?',
        createdAt: moment(),
        updatedAt: moment()
    }
];

export const IntakeFormPanel: FunctionComponent<IntakeFormPanelProps> = ({ isOpen, onClose, onOpenShareSendPanel }) => {
    const dispatch = useDispatch();
    const marketplace = useMarketplace();
    const apiClientWrapper = useApiClientWrapper();
    const { isOwner, isManager } = useRoles();
    const isOwnerOrManager = isOwner || isManager;

    const [urlCopied, setUrlCopied] = useState(false);
    const [buttonIcon, setButtonIcon] = useState(faCopy);
    const [buttonMessage, setButtonMessage] = useState('Copy');
    const [enabled, setEnabled] = useState(marketplace.intakeForm.enabled);
    const [customQuestionsEnabled, setCustomQuestionsEnabled] = useState(marketplace.intakeForm.questionsEnabled);
    const [customQuestions, setCustomQuestions] = useState<IntakeFormCustomQuestion[]>(
        marketplace.intakeForm.questions
    );
    const [deletedQuestionIds, setDeletedQuestionIds] = useState<number[]>([]);
    const [attemptedSave, setAttemptedSave] = useState(false);
    const [showExitModal, setShowExitModal] = useState(false);

    // Store initial state to detect changes
    const initialEnabledRef = useRef(marketplace.intakeForm.enabled);
    const initialQuestionsRef = useRef<IntakeFormCustomQuestion[]>(marketplace.intakeForm.questions);
    const initialQuestionsEnabledRef = useRef(marketplace.intakeForm.questionsEnabled);

    const intakeFormUrl = `${process.env.REACT_APP_ONLINE_BOOKING_HOST_URL}${marketplace.onlineBooking.slug}/intake-form`;

    // Check if there are unsaved changes
    const hasUnsavedChanges = (): boolean => {
        // Check if enabled toggle changed
        if (enabled !== initialEnabledRef.current) {
            return true;
        }

        // Check if custom questions enabled changed
        const initialQuestionsEnabled = initialQuestionsEnabledRef.current;
        if (customQuestionsEnabled !== initialQuestionsEnabled) {
            return true;
        }

        // Check if questions changed (content, type, required flag)
        if (!questionsEqual(customQuestions, initialQuestionsRef.current)) {
            return true;
        }

        // Check if any questions were deleted
        if (deletedQuestionIds.length > 0) {
            return true;
        }

        return false;
    };

    // Snapshot intake form state only when the panel opens. Card on file (and other
    // marketplace saves) replace settings/questions in Redux and must not reset this switch.
    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const initialQuestions = marketplace.intakeForm.questions;
        setCustomQuestions(initialQuestions);
        setDeletedQuestionIds([]);
        setAttemptedSave(false);
        setShowExitModal(false);
        setEnabled(marketplace.intakeForm.enabled);
        setCustomQuestionsEnabled(marketplace.intakeForm.questionsEnabled);
        initialEnabledRef.current = marketplace.intakeForm.enabled;
        initialQuestionsRef.current = initialQuestions;
        initialQuestionsEnabledRef.current = marketplace.intakeForm.questionsEnabled;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    useEffect(() => {
        if (urlCopied) {
            setButtonMessage('Copied');
            setButtonIcon(faCheck);
        }
    }, [urlCopied]);

    // This function is called before the drawer attempts to close
    const handleBeforeClose = (): boolean => {
        // Check for unsaved changes
        if (hasUnsavedChanges()) {
            // Show modal to confirm exit
            setShowExitModal(true);
            // Return false to prevent closing
            return false;
        }
        // No changes, allow close
        return true;
    };

    const changeEnableHandler = (checked: boolean) => {
        setEnabled(checked);
    };

    const copyToClipboard = () => {
        setUrlCopied(true);
        if ('clipboard' in navigator) {
            navigator.clipboard.writeText(intakeFormUrl);
        } else {
            document.execCommand('copy', true, intakeFormUrl);
        }
    };

    const toggleQuestionsHandler = (enabled: boolean) => {
        setCustomQuestionsEnabled(enabled);

        if (enabled && customQuestions.length === 0) {
            const defaultQuestions = DEFAULT_QUESTIONS(Number(marketplace.id));
            setCustomQuestions(defaultQuestions);
        }
    };

    const saveQuestionsHandler = (enabled: boolean, questions: IntakeFormCustomQuestion[], deletedIds: number[]) => {
        setCustomQuestionsEnabled(enabled);
        setCustomQuestions(questions);
        setDeletedQuestionIds(deletedIds);
    };

    const handleSave = () => {
        setAttemptedSave(true);

        // Validate custom questions before saving
        if (customQuestionsEnabled && customQuestions.length === 0) {
            return; // Don't save if validation fails
        }

        // Check for empty questions
        const hasEmptyQuestions = customQuestions.some(q => !q.question.trim());
        if (customQuestionsEnabled && hasEmptyQuestions) {
            return; // Don't save if validation fails
        }

        // Save intake form status if it changed
        if (enabled !== marketplace.intakeForm.enabled) {
            dispatch(changeIntakeFormStatusThunk(apiClientWrapper, enabled));
        }

        // Save custom questions if they changed
        if (
            customQuestionsEnabled !== marketplace.intakeForm.questionsEnabled ||
            JSON.stringify(customQuestions) !== JSON.stringify(marketplace.intakeForm.questions) ||
            deletedQuestionIds.length > 0
        ) {
            dispatch(
                saveIntakeFormCustomQuestionsThunk(
                    apiClientWrapper,
                    customQuestionsEnabled,
                    customQuestions,
                    deletedQuestionIds
                )
            );
        }

        // Update initial state after successful save
        initialEnabledRef.current = enabled;
        initialQuestionsRef.current = customQuestions;
        initialQuestionsEnabledRef.current = customQuestionsEnabled;

        // Close the panel
        onClose();
    };

    const handleKeepEditing = () => {
        setShowExitModal(false);
    };

    const handleExit = () => {
        // Reset to initial state
        setEnabled(initialEnabledRef.current);
        setCustomQuestions(initialQuestionsRef.current);
        setCustomQuestionsEnabled(initialQuestionsEnabledRef.current);
        setDeletedQuestionIds([]);
        setAttemptedSave(false);
        setShowExitModal(false);
        // Now close the panel
        onClose();
    };

    const confirmCloseDialog = (
        <ConfirmClosePopup
            title="Exit Intake Form"
            question="Your changes will not be saved. Are you sure you want to exit this intake form?"
            onDiscard={handleKeepEditing}
            onSave={handleExit}
        />
    );

    const handleSendLink = () => {
        if (onOpenShareSendPanel) {
            onOpenShareSendPanel();
        }
    };

    // Component to render close button inside drawer (for mobile)
    const handleMobileClose = () => {
        if (handleBeforeClose()) {
            handleExit();
        }
    };

    const closeButton = <MobileCloseButton onClick={handleMobileClose} />;

    // Don't render anything if panel should be closed
    if (!isOpen) return null;

    return (
        <>
            {showExitModal && confirmCloseDialog}
            <Drawer
                onBeforeClose={handleBeforeClose}
                onClose={handleExit}
            >
                <div className={styles.panel}>
                    {/* Header with Title and Switch */}
                    <div className={styles.header}>
                        <div className={styles.leftGroup}>
                            {' '}
                            {/* <-- Nuevo contenedor para agrupar */}
                            <h2 className={styles.title}>Intake Form</h2>
                            {isOwnerOrManager && (
                                <Switch
                                    checked={enabled}
                                    onChange={checked => changeEnableHandler(checked)}
                                ></Switch>
                            )}
                        </div>
                        {closeButton}
                    </div>

                    {/* Content based on enabled state */}
                    {!enabled ? (
                        <div className={styles.disabledMessage}>
                            <AlertMessage
                                icon={faInfoCircle}
                                bgColor="#F8F5F1"
                                bdColor="#BAA997"
                            >
                                Your intake form is disabled. Enable to allow pet parents to create client profiles
                                online.
                            </AlertMessage>
                        </div>
                    ) : (
                        <>
                            <div className={styles.alertContainer}>
                                <AlertMessage
                                    icon={faCircleCheck}
                                    bgColor="#F8F5F1"
                                    bdColor="#BAA997"
                                >
                                    Your intake form is currently enabled, allowing pet parents to create client
                                    profiles effortlessly.
                                </AlertMessage>
                            </div>

                            {/* URL Section */}
                            <div className={styles.subtitle}>
                                Share your intake form link today to connect with clients and efficiently create their
                                profiles
                            </div>

                            <div className={styles.urlContainer}>
                                <div className={styles.urlInput}>
                                    <TextFieldWithButton
                                        value={intakeFormUrl}
                                        label={buttonMessage}
                                        startIcon={buttonIcon}
                                        blackButton={true}
                                        onClick={copyToClipboard}
                                        classNameCustom="onlineBookingInput"
                                        readOnly={true}
                                    />
                                    <Button
                                        label="Send Link"
                                        variant="primary"
                                        size="medium"
                                        onClick={handleSendLink}
                                        className={styles.sendLinkButton}
                                    />
                                </div>
                            </div>

                            <CardOnFile disabled={!isOwnerOrManager} />

                            {/* Custom Questions Section */}
                            <div className={styles.customQuestionsSection}>
                                <CustomQuestions
                                    onEnable={toggleQuestionsHandler}
                                    onSave={saveQuestionsHandler}
                                    initialQuestions={customQuestions}
                                    initialEnabled={customQuestionsEnabled}
                                    marketplaceId={Number(marketplace.id)}
                                    attemptedSave={attemptedSave}
                                />
                            </div>
                        </>
                    )}
                </div>
                {/* Save Button */}
                <div className={styles.footer}>
                    <Button
                        label="Save"
                        onClick={handleSave}
                        variant="green"
                        size="large"
                        fullWidth={true}
                    />
                </div>
            </Drawer>
        </>
    );
};

export default IntakeFormPanel;
